import { ReactNode, FocusEventHandler, Key, useCallback, useEffect, useMemo, useState } from 'react';

import { TreeComboBoxContents } from './tree-combo-box-contents';
import { deselect, filterData, getKeysFromMenuItems, getMenuItemsFromSelection } from './tree-combo-box-utils';
import { ComboBoxCore } from '../combo-box/combo-box-core';
import { ComboBoxContainerSize } from '../combo-box/combo-box.types';
import { HighlightedMenuItem } from '../filter-combo-box/filter-combo-box.types';
import { ErrorTextProps } from '../form-helper-text/form-helper-text';
import { TreeData } from '../tree/tree.types';

export interface TreeComboBoxProps {
  data: TreeData[];
  open?: boolean;
  placeholder?: string;
  size?: ComboBoxContainerSize;
  value?: Set<Key>;
  isLoading?: boolean;
  onChange: (selected: Set<Key>) => void;
  onDeselect?: (item: HighlightedMenuItem) => void;
  onSearch?: (searchText: string) => void;
  searchText?: string;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  bottomOptionsListContent?: ReactNode;
  errorProps?: ErrorTextProps;
  /** Ref to the bottom of the dropdown. Useful for infinite scroll */
  bottomRef?: (node?: Element | null | undefined) => void;
}

// eslint-disable-next-line max-lines-per-function
export const TreeComboBox = (props: TreeComboBoxProps) => {
  const {
    data,
    bottomRef,
    isLoading = false,
    searchText: defaultSearchTerm = '',
    onChange,
    onDeselect: onDeselectCallback,
    onSearch: onSearchCallback,
    value,
    bottomOptionsListContent,
    size,
    ...rest
  } = props;
  const [selectedKeys, setSelectedKeys] = useState<Set<Key>>(() => value ?? new Set<Key>());
  const [searchText, setSearchText] = useState<string>(defaultSearchTerm);

  useEffect(() => setSearchText(defaultSearchTerm), [defaultSearchTerm]);
  useEffect(() => {
    if (value) setSelectedKeys(value);
  }, [value]);

  const onSearch = useCallback(
    (searchText: string) => {
      setSearchText(searchText);
      onSearchCallback?.(searchText);
    },
    [onSearchCallback]
  );

  const onSelect = useCallback(
    (selection: Set<Key>) => {
      const keys = new Set<Key>(selection);
      setSelectedKeys(keys);
      onChange(selection);
    },
    [onChange]
  );

  const onDeselect = useCallback(
    (item: HighlightedMenuItem) => {
      const newKeys = deselect(item.value, selectedKeys, data);
      onSelect(newKeys);
      onDeselectCallback?.(item);
    },
    [data, onDeselectCallback, onSelect, selectedKeys]
  );

  const filteredData = useMemo(() => filterData(data, searchText), [data, searchText]);
  const selectedMenuItems = useMemo(() => getMenuItemsFromSelection(selectedKeys, data), [selectedKeys, data]);
  const defaultExpandedKeys = useMemo(() => getKeysFromMenuItems(data), [data]);

  return (
    <ComboBoxCore<HighlightedMenuItem>
      {...rest}
      searchTerm={searchText}
      onSearch={onSearch}
      defaultSelection={selectedMenuItems}
      onDeselect={onDeselect}
      containerSize={size}
      menuContent={
        <TreeComboBoxContents
          bottomRef={bottomRef}
          displayDropdown={filteredData.length > 0}
          isLoading={isLoading}
          defaultExpandedKeys={defaultExpandedKeys}
          treeData={data}
          searchTerm={searchText}
          checkedKeys={selectedKeys}
          onCheck={onSelect}
          bottomContent={bottomOptionsListContent}
        />
      }
    />
  );
};

export default TreeComboBox;
